import {
  Box,
  Flex,
  Heading,
  Input,
  Text,
  useDisclosure
} from "@chakra-ui/react"
import { useCallback, useContext, useEffect, useState } from "react"
import { toast } from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { IoWarning } from "react-icons/io5"
import { MdDelete, MdDeleteOutline } from "react-icons/md"
import { useHistory } from "react-router-dom"
import { useRecoilState, useRecoilValue } from "recoil"
import { SectionContainer } from "src/components/common/SectionContainer"
import { Routes } from "src/constants"
import { SDKContext, sdkContextStatus } from "src/context/SDKContext"

import { PlansPageStyling } from "src/context/types"
import useAuthedUser from "src/hooks/useAuthedUser"
import { useChatGenerationValues } from "src/hooks/useChatGenerationValues"
import useConfig from "src/hooks/useConfig"
import usePageStyling from "src/hooks/usePageStyling"
import { nativeNotificationStateAtom } from "src/recoil/atoms/account"
import { avatarsSelector } from "src/recoil/atoms/avatars"
import { AvatarDetail } from "src/recoil/atoms/avatars/types"
import axios from "src/shared/axios"
import { useIsMobileDevice } from "src/util/browser"
import { useIsMobileApp } from "src/util/deviceType"
import { useRecentChats } from "../avatars/hooks/useRecentChats"
import { PlansButton } from "../plans/components/PlansButton"
import { AccountLayout } from "./components/AccountLayout"
import { PopupModal, PopupModalButton } from "./components/PopupModal"
import { Switch } from "./components/Switch"

export const Account = () => {
  const isMobileApp = useIsMobileApp()
  const isMobile = useIsMobileDevice()
  const plansPageStyles: PlansPageStyling = usePageStyling("plansPageStyling")
  const { t } = useTranslation()
  const {
    isOpen: isOpenClearChats,
    onOpen: onOpenClearChats,
    onClose: onCloseClearChats
  } = useDisclosure()
  const {
    isOpen: isOpenDeleteAccount,
    onOpen: onOpenDeleteAccount,
    onClose: onCloseDeleteAccount
  } = useDisclosure()
  const { invalidateRecentChats } = useRecentChats()

  const { authedUser, getAuthedUser } = useAuthedUser()
  const { getChatGenerationValues, putChatGenerationValue } =
    useChatGenerationValues()
  const chatGenerationValues = getChatGenerationValues()
  const { avatars } = useRecoilValue(avatarsSelector) || ({} as AvatarDetail)
  const history = useHistory()

  return (
    <AccountLayout>
      <PopupModal
        isOpen={isOpenClearChats}
        branding={plansPageStyles}
        onClose={onCloseClearChats}
        title={t("Clear chat history")}
        description={t(
          "This irreversibly will clear your chat history and the memories of all avatars."
        )}
        action={
          <PopupModalButton
            onClick={async () => {
              const authedUser = await getAuthedUser()
              const createdCustomerAvatarIds = chatGenerationValues?.map(
                c => c.customerAvatarId
              )
              const notYetCreated = avatars?.filter(
                a => !createdCustomerAvatarIds?.includes(a.id)
              )

              const p1 =
                chatGenerationValues?.map(async cgv =>
                  putChatGenerationValue({
                    ...cgv,
                    generation: cgv.generation + 1
                  })
                ) ?? []

              const p2 =
                notYetCreated?.map(async n =>
                  putChatGenerationValue({
                    customerAvatarId: n.id,
                    generation: 1,
                    ownerId: authedUser.userId
                  })
                ) ?? []
              onCloseClearChats()

              toast.promise(
                Promise.all([...p1, ...p2]).then(invalidateRecentChats),
                {
                  loading: "Loading...",
                  error: "Something went wrong",
                  success: "Chats cleared"
                }
              )
            }}
          >
            {t("Clear")}
          </PopupModalButton>
        }
      />

      <PopupModal
        isOpen={isOpenDeleteAccount}
        branding={plansPageStyles}
        onClose={onCloseDeleteAccount}
        title={t("Delete account")}
        description={t(
          "This will irreversibly delete your account. You will need to cancel any ongoing subscriptions yourself."
        )}
        action={
          <PopupModalButton
            onClick={async () => {
              const meetingClient = await sdkContextStatus
              const url = new URL(meetingClient?.envConfig.USER_REGISTRATION)
              url.searchParams.append(
                "host",
                meetingClient?.envConfig.DOMAIN_HOSTNAME
              )
              const response = await axios.delete(url.toString(), {
                withCredentials: true
              })
              onCloseDeleteAccount()
              history.replace(Routes.LOGIN)
            }}
          >
            {t("Delete")}
          </PopupModalButton>
        }
      />
      <SectionContainer>
        <Flex direction={"column"} gap="5px">
          <ScreenNameSettings />
          {isMobileApp && <NotificationSettings />}
          <Box padding={"5px"} />

          <PlansButton
            isLandscape={false}
            isMobile={isMobile}
            onClick={onOpenClearChats}
          >
            <Flex>
              <Text
                color={plansPageStyles?.planPageButtonTextColor}
                // fontWeight={"semibold"}
                whiteSpace="nowrap"
                textOverflow={"ellipsis"}
                overflow={"hidden"}
                fontSize={"18px"}
              >
                Clear all chats
              </Text>
              <Box marginLeft={"auto"} paddingTop={"3px"} paddingLeft="5px">
                <MdDeleteOutline
                  color={plansPageStyles?.planPageButtonTextColor}
                />
              </Box>
            </Flex>
          </PlansButton>

          <PlansButton
            isLandscape={false}
            isMobile={isMobile}
            onClick={onOpenDeleteAccount}
          >
            <Flex>
              <Text
                color={plansPageStyles?.planPageButtonTextColor}
                // fontWeight={"semibold"}
                whiteSpace="nowrap"
                textOverflow={"ellipsis"}
                overflow={"hidden"}
                fontSize={"18px"}
              >
                Delete Account
              </Text>
              {/* <Box marginLeft={"auto"} paddingTop={"3px"} paddingLeft="5px">
                <MdDeleteOutline
                  color={plansPageStyles?.planPageButtonTextColor}
                />
              </Box> */}
            </Flex>
          </PlansButton>
        </Flex>
      </SectionContainer>
    </AccountLayout>
  )
}

const ScreenNameSettings = () => {
  const { t } = useTranslation()
  const plansPageStyles: PlansPageStyling = usePageStyling("plansPageStyling")
  const { authedUser, getAuthedUser, updateAuthedUserAsync } = useAuthedUser({
    onMutationSuccess: () => {
      getAuthedUser() // NOTE: there might be a way to avoid having to do this
    }
  })
  const [screenName, setScreenName] = useState("")
  useEffect(() => {
    getAuthedUser()
  }, [])

  useEffect(() => {
    if (authedUser?.screenName) {
      setScreenName(authedUser?.screenName)
    }
  }, [authedUser?.screenName])

  return (
    <>
      <Heading
        pt={5}
        color={plansPageStyles?.planPagePanelTextColor}
        fontSize="20px"
        fontWeight={"medium"}
      >
        {t("Profile")}
      </Heading>
      <Flex align="baseline" direction="row" justify="space-between" gap="10px">
        <Text
          color={plansPageStyles?.planPagePanelSubTextColor}
          whiteSpace="nowrap"
        >
          {t("Email")}
        </Text>
        <Text
          flexGrow={1}
          textAlign={"right"}
          maxWidth={"60%"}
          // maxWidth={isMobileDevice(screenSize) ? "60%" : "unset"}
          color={plansPageStyles?.planPagePanelSubTextColor}
          whiteSpace="nowrap"
          textOverflow={"ellipsis"}
          overflow={"hidden"}
        >
          {authedUser?.email}
        </Text>
      </Flex>
    </>
  )
}

const NotificationSettings = () => {
  const plansPageStyles: PlansPageStyling = usePageStyling("plansPageStyling")
  const { t } = useTranslation()
  const [notificationState, setNotificationState] = useRecoilState(
    nativeNotificationStateAtom
  )
  const meetingClient = useContext(SDKContext)
  const { isOpen, onOpen, onClose } = useDisclosure()

  const dispatchStateEventToNative = useCallback(
    ({
      enabled,
      timeScheduledAt
    }: {
      enabled: boolean
      timeScheduledAt: string
    }) => {
      meetingClient?.nativeBridge?.eventReactNotificationSettingsUpdated({
        enabled,
        timeScheduledAt
      })
    },
    [meetingClient]
  )

  return (
    <>
      <PopupModal
        isOpen={isOpen}
        branding={plansPageStyles}
        onClose={onClose}
        title={t("Permission not granted")}
        description={t(
          "To enable notifications you must grant notification permissions in your device settings."
        )}
      />
      <Flex pt={5} direction={"row"} justify="space-between" align="center">
        <Heading
          color={plansPageStyles?.planPagePanelTextColor}
          fontSize="20px"
          fontWeight={"medium"}
        >
          {t("Notifications")}
        </Heading>
        {!notificationState.permissionGranted && (
          <Box
            onClick={() => onOpen()}
            // bg="transparent"
            // _hover={{ bg: "transparent" }}
            // _focus={{ bg: "transparent" }}
            // _active={{ bg: "transparent" }}
          >
            <IoWarning
              size={"30"}
              color={plansPageStyles?.planPageWarningColor}
            />
          </Box>
        )}
      </Flex>
      <Flex align="baseline" direction="row" justify="space-between" gap="10px">
        <Text
          color={plansPageStyles?.planPagePanelSubTextColor}
          whiteSpace="nowrap"
        >
          {t("Daily Reminder")}
        </Text>
        <Switch
          disabled={!notificationState?.permissionGranted}
          onChange={
            !notificationState?.permissionGranted
              ? null
              : () => {
                  const newState = {
                    ...notificationState,
                    enabled: !notificationState.enabled
                  }
                  setNotificationState(newState)
                  dispatchStateEventToNative(newState)
                }
          }
          isChecked={notificationState?.enabled}
          styles={plansPageStyles}
        ></Switch>
      </Flex>
      <Flex align="baseline" direction="row" justify="space-between" gap="10px">
        <Text
          color={plansPageStyles?.planPagePanelSubTextColor}
          whiteSpace="nowrap"
        >
          {t("Reminder Time")}
        </Text>
        <Input
          flexGrow={1}
          maxWidth={"60%"}
          disabled={!notificationState?.permissionGranted}
          size="md"
          type="time"
          color={plansPageStyles?.planPagePanelSubTextColor}
          value={notificationState?.timeScheduledAt}
          onChange={event => {
            const time = event.target.value
            const newState = { ...notificationState, timeScheduledAt: time }
            setNotificationState(newState)
            dispatchStateEventToNative(newState)
          }}
        />
      </Flex>
    </>
  )
}
